// What the web view's JSON API returns. Shared by the server (src/web/api.ts) and the browser code
// (web/src), so both sides agree on one shape.
import type { CurrencyInUse } from "../currency.ts";
import type { Estimate } from "../estimate.ts";
import type { Budget, ItemFields, ShownPreview, When } from "../model.ts";
import type { Amount, ComingCharge, Spending } from "../money.ts";
import type { Share } from "../sharing/switches.ts";
import type { TakeoverNeeds } from "../takeover-words.ts";
import type { WeatherSymbol } from "../weather/forecast.ts";

export type { When, Where } from "../model.ts";
export type { DatesHint } from "../trip-dates.ts";
export type { WeatherSymbol } from "../weather/forecast.ts";

// What "via" says for an AI that gave no name when it registered: never its raw client id.
export const UNNAMED_AI = "an AI";

// How long a diet or a mobility can be, in the app's Diet and mobility (Trip settings).
export const MAX_NEED = 500;

export type PersonColor = "amber" | "teal" | "coral" | "violet" | "leaf" | "sky";

export interface Person {
  id: string;
  name: string;
  color: PersonColor;
  // Their membership in the trip (mem_…), which the owner's actions address. Absent outside a trip.
  member?: string;
  // Whether they share their location with the trip (during it).
  sharing?: boolean;
  // Their photo, when they have one (Souvenir's avatars show it instead of initials).
  src?: string;
  // A former member: their account was deleted (features/privacy-and-data.md). Named "a former member",
  // with no photo; what they added stays theirs.
  former?: true;
  // One of the sample trip's imaginary people (features/sample-trip.md), in a working copy of it: never signs in, so
  // never handed the trip.
  sample?: true;
  // On a shared trip's view (features/sharing.md): a member who hasn't allowed their name, photo and ratings to show
  // (or with names off), "A traveller", in their colour only.
  anonymous?: true;
}

// Where someone sharing with the trip last was.
export interface PersonLocation {
  person: string;
  name: string;
  lat: number;
  lng: number;
  accuracy_m?: number;
  at: string;
}

export interface Me {
  // My colour is the one my face takes where no one trip is in view (Your trips, the profile): from the
  // trip I joined last (store.faceColor); null in no trip, when Souvenir's default shows.
  user: { id: string; name: string; email: string; photo: string | null; color: PersonColor | null };
  // What to add as a connector in an AI.
  mcp_url: string;
  map: { tiles: string; glyphs: string };
  // Each AI I've connected, the one used last first. None yet: the web view shows only getting started,
  // How Tripmo works then how to connect one (features/getting-started.md).
  ais: AiConnection[];
  // What getting started has shown me once and put away, on every device (TIPS).
  tips_seen: Tip[];
  // The time zone my device last said (due-date reminders and the weekly round-up go at 9:00 in it); null
  // until one has. The web view says its own when it differs.
  time_zone: string | null;
  // My currency (the profile's Your currency): my account's, one for all my trips, the one Money's estimates are in
  // but where my member profile in a trip says another (src/currency.ts); null until one is chosen. Missing only from
  // an answer a phone kept offline from before.
  currency?: string | null;
}

// My currency set on the profile: the account's now, and the one it replaced (for Undo at once; null: none).
export interface CurrencySet {
  currency: string | null;
  was: string | null;
}

// 16 or older, and the Terms, once (src/auth/terms.ts): what the screen asks about, carried by the web API's
// refusal (`terms_needed`, 403) until it's done. `version`: the Terms the ticks agree to (sent back with them);
// `changes`: what changed since the version I agreed to before, a sentence each (none the first time).
export interface TermsAsked {
  version: string;
  changes: string[];
}

// Your trips: my trips, those I deleted that I can still restore (Recently deleted), and my face's
// colour as it is now (Me's `color`, kept up with the trips); my Trip Passes waiting to be used (each a
// placeholder card, first in the grid; none while I'm on a plan) and what paying allows me now
// (features/paying.md).
export interface TripList {
  trips: TripCard[];
  deleted: DeletedTrip[];
  color: PersonColor | null;
  passes: PassCard[];
  billing: BillingSummary;
  // My requests to join that the owner turned down, not said yet: Your trips says each once
  // (features/trips-and-members.md, "Withdraw a join request, and hear a turn-down").
  turned_down: TurnedDown[];
}

export interface TurnedDown {
  id: string;
  title: string;
  dates: When;
  // The trip's owner, who turned it down.
  owner: string;
  at: string;
}

// A Trip Pass bought and not used yet: a placeholder on Your trips, "Start this trip" (spec §8.1b).
// A code redeemed for a free Trip Pass (features/paying.md, "Free Trip Passes from codes"): the pass, waiting on Your
// trips, and the words saying so; or, for an invite's code (FAM-, PTR-), its own page, which redeems it.
export type CodeRedeemed =
  | { redeemed: true; pass: { id: string }; message: string }
  | { redeemed: false; invite: string };

export interface PassCard {
  id: string;
  bought_at: string;
  // The sign-up gift's (features/paying.md, "A free first trip for every sign-up"): "Your first trip, on us".
  gift: boolean;
  // How many people it covers, owner included.
  seats: number;
  // Until when it can be refunded (bought 14 days ago at most), else null.
  refundable_until: string | null;
  refund_requested: boolean;
  // Its latest refund request (open, approved, refunded or declined), or none.
  refund_request: RefundRequestState | null;
}

// A refund asked for, as its asker sees it (features/paying.md, "Refunds"): waiting for a person at Tripmo
// (open), approved and on its way, refunded, or declined with their note.
export interface RefundRequestState {
  id: string;
  target_type: "trip_pass" | "seat_addon" | "entitlement";
  target_id: string;
  status: "open" | "approved" | "rejected" | "refunded" | "failed" | "withdrawn";
  eligible: boolean;
  amount: number;
  currency: string;
  created_at: string;
  note: string | null;
}

// What POST /api/me/refund-requests answers: the request kept, whether it's eligible, and what to tell
// the person (why not, when it isn't).
export interface RefundAsked {
  request: RefundRequestState;
  eligible: boolean;
  message: string;
}

// A person's account plan, as list_trips' billing names it.
export type PlanKind = "yearly" | "lifetime" | "comp_lifetime" | "partner";

// What paying allows me now, for Your trips' buttons: my plan (or none), whether a trip can be started
// now, whether this server sells (Stripe on: the Get a Trip Pass buttons), the prices, and fair use while
// I'm on a plan (New trip says how many are left near its end).
export interface BillingSummary {
  plan: PlanKind | "none";
  // "Frequent Flyer", "Lifetime" (a comp too), "Partner"; null with no plan.
  plan_name: string | null;
  can_create_trip: boolean;
  buying: boolean;
  prices: { trip_pass: string; frequent_flyer: string };
  fair_use: {
    per_day: { used: number; max: number; resets_at: string | null };
    active: { used: number; max: number };
  } | null;
}

// Profile's Billing (GET /api/me/billing): the plan and its state, every pass and add-on with theirs,
// receipts, and where to write (spec §8.4).
export interface MyBilling {
  summary: BillingSummary;
  plan: {
    kind: PlanKind;
    name: string;
    status: "active" | "past_due";
    // When it ends (a yearly plan's paid-through date); null: no end.
    ends_at: string | null;
    // Paid by a Stripe subscription: Manage subscription opens Stripe's portal.
    subscription: boolean;
    // A subscription's next step at ends_at: it renews, or it's been cancelled and ends then; null for a
    // plan with no subscription.
    renewal: "renews" | "cancels" | null;
    // The plan paid for (Frequent Flyer's first year, Lifetime), to refund it: its id, whether it can be
    // now (no trip started on it, within 14 days), and its latest request.
    id: string;
    refund: { until: string; why: string } | null;
    refund_request: RefundRequestState | null;
  } | null;
  passes: OwnedPass[];
  addons: OwnedAddon[];
  receipts: Receipt[];
  support_email: string;
  // For partners: their code, link and what they've earned (spec §8.4); null for everyone else.
  partner: PartnerPanel | null;
  // What abuse review restricts on the account now (features/paying.md, "Abuse review"), oldest first, in
  // words, and until when (null: until a person at Tripmo lifts it). Empty for nearly everyone.
  restrictions: {
    kind: "no_create" | "no_purchase" | "no_self_refund" | "no_partner_payouts" | "suspended";
    words: string;
    since: string;
    until: string | null;
  }[];
}

// A partner's own view of their partnership (features/paying.md, "Billing on Profile"). Amounts in minor units.
export interface PartnerPanel {
  // pending: their invite isn't accepted yet (never shown: accepting makes them active); ended: what's owed
  // is still paid.
  status: "pending" | "active" | "ended";
  // The code people type (ALEX); null once it's turned off.
  code: string | null;
  // What the code takes off, and their share of a new customer's first payment.
  discount_percent: number;
  commission_percent: number;
  // https://tripmo.me/?ref=<handle>
  share_link: string;
  // First payments credited to them (voided ones left out), and what those buyers paid.
  sales: number;
  sales_amount: number;
  // Commission held for its 30 days, payable since, and paid.
  held: number;
  payable: number;
  paid: number;
  currency: string;
  payouts: { period: string; net: number; status: "paid" | "on_its_way"; paid_at: string | null }[];
  // A payout's least: under it, a month carries forward.
  minimum: number;
}

// A Trip Pass of mine, whatever became of it.
export interface OwnedPass {
  id: string;
  // When it was bought, or a free one redeemed.
  bought_at: string;
  // A free Trip Pass, from a code (features/paying.md, "Free Trip Passes from codes") or the sign-up gift: nothing
  // was paid, so it has no receipt and no refund.
  free: boolean;
  // The sign-up gift's (features/paying.md, "A free first trip for every sign-up"): "a gift for signing up".
  gift: boolean;
  // waiting: a placeholder on Your trips; saved: kept for when my plan ends (hidden while it's on);
  // used: covers `trip`; refunded; disputed: on hold while a chargeback is open.
  state: "waiting" | "saved" | "used" | "refunded" | "disputed";
  // The trip it covers (null once that trip is gone for good).
  trip: { id: string; title: string; deleted: boolean } | null;
  // Refundable now, until when and why (the server's check); null when it isn't.
  refund: { until: string; why: string } | null;
  refund_requested: boolean;
  // Its latest refund request (open, approved, refunded or declined), or none.
  refund_request: RefundRequestState | null;
}

// Spots I added to a trip.
export interface OwnedAddon {
  id: string;
  trip: { id: string; title: string } | null;
  blocks: number;
  bought_at: string;
  status: "active" | "refunded" | "disputed";
  used: boolean;
  refund: { until: string; why: string } | null;
  refund_request: RefundRequestState | null;
}

// One payment, with its receipt (`url` sends on to Stripe's receipt or invoice).
// A receipt as its buyer paid it: in their own currency, in its minor units (none for yen).
export interface Receipt {
  id: string;
  kind: "trip_pass" | "seat_addon" | "yearly" | "yearly_renewal" | "lifetime";
  amount: number;
  refunded: number;
  currency: string;
  paid_at: string;
  status: "paid" | "partially_refunded" | "refunded" | "disputed" | "dispute_lost";
  url: string;
}

// The trip the web made (Start this trip, New trip): where it opens.
export interface StartedTrip {
  trip: { id: string; url: string; join_link: string };
}

// What getting started shows once per person, until it's put away for good, on every device
// (features/getting-started.md): How Tripmo works, the four first screens (seen once, before connecting);
// connecting an AI, skipped for now (Skip for now: getting started doesn't come back, on any device); Your
// trips' line saying how to start a trip of one's own by connecting, for someone without an AI (put away by
// Not now); the first trip's card standing out on Your trips (until it's opened); and the tips inside a
// trip ("Just in time, inside the trip"): each view's first-visit card, and the line that explains the
// rating buttons.
export const TIPS = [
  "how_tripmo_works",
  "skip_connecting",
  "connect_to_start",
  "first_trip",
  "map",
  "days",
  "ideas",
  "people",
  "bookings",
  "updates",
  "rating",
] as const;
export type Tip = (typeof TIPS)[number];

// My trips as they are now, on my own event stream (`trips`): enough to tell whether Your trips is
// showing them as they are. `updated_at` moves with every change to the trip.
export interface TripMark {
  id: string;
  status: "member" | "requested";
  updated_at: string;
}

// My event stream's `trips`: my trips as they are now, with my waiting passes and plan, so a placeholder
// appearing or going (bought, used, refunded) or a plan starting or ending reloads Your trips too.
export interface TripsHeard {
  trips: TripMark[];
  passes: string[];
  plan: PlanKind | "none";
}

// An AI I've connected: the name it gave itself (null when it gave none), when it connected (I allowed
// it on Tripmo's allow page, or it first called), and its last call (to within a few minutes; null until
// it first calls).
export interface AiConnection {
  client: string;
  name: string | null;
  first_at: string;
  last_at: string | null;
}

// Sign out of other devices, done (features/profile.md): how many browsers were signed out (sessions that hadn't run
// out), and how many devices' notifications stopped.
export interface SignedOutElsewhere {
  signed_out: number;
  devices: number;
}

// Notifications on this server, and which kinds I want (the same on every device I turned them on).
export interface NotificationSettings {
  // Whether this server can send them (it has its key pair), and the key browsers subscribe with.
  available: boolean;
  key: string | null;
  kinds: {
    reminders: boolean;
    waiting: boolean;
    answers: boolean;
    changes: boolean;
    todos: boolean;
    ratings: boolean;
    // Due-date reminders: pushed, and by email.
    due: boolean;
    // The weekly round-up, by email only.
    round_up: boolean;
    // Offers from Tripmo, by email only: now and then an offer for the account (features/notifications.md).
    offers: boolean;
    // The admin's alerts: only for its owners (absent for everyone else).
    alerts?: boolean;
  };
}

export interface TripCard {
  id: string;
  title: string;
  dates: When;
  // "requested": asked to join and waiting; the rest of the card is empty until the owner lets them in.
  status: "member" | "requested";
  owner: Person | null;
  members: Person[];
  // What it's made of: places (and goals), stays and journeys in the plan.
  counts: { places: number; stays: number; moves: number };
  booked: number;
  // What waits on me in it (src/waits.ts): the due dates of my open to-dos (mine or nobody's), soonest
  // first, for the card to count those within 7 days or overdue by my own today; how many places and
  // goals wait on my rating; and the questions waiting on me. Missing only from an answer a phone kept
  // offline from before.
  waiting?: { due: string[]; to_rate: number; questions: number };
  // Read-only (its Trip Pass refunded or disputed, or its refund on its way: the trip's own `locked`), which
  // its card says. Missing only from an answer a phone kept offline from before.
  locked?: boolean;
  // Only for the owner, who decides: people waiting to be let in.
  requests: Person[];
  // Offered to me by its owner, to take over (DESIGN.md, Member, "Taking over a trip"): who offered it and until when.
  // Missing when it isn't, and from an answer a phone kept offline from before.
  offer?: { by: string; until: string } | null;
  // Its last change, so Your trips can tell when it's behind (`trips` on my event stream).
  updated_at: string;
  // For the card's little map: where its places and stays are, [lng, lat], and its journeys, from
  // [lng, lat] to [lng, lat].
  points: [number, number][];
  routes: [[number, number], [number, number]][];
  // My first trip, not opened from Your trips yet: its card stands out until it is.
  first_unopened: boolean;
  // Shared to view and asking me whether viewers see my name, photo and ratings (features/sharing.md, "Each member
  // allows their own"): until I choose, on its card.
  viewers_ask?: true;
}

export interface TripView {
  id: string;
  title: string;
  dates: When;
  // What its name and dates are changed over (Trip settings). Missing only from an answer a phone kept offline
  // from before.
  version?: number;
  owner: string;
  you: string;
  members: Person[];
  requests: Person[];
  // Those whose account was deleted, as "a former member" each: authors of what they added. Missing
  // only from an answer a phone kept offline from before.
  former?: Person[];
  // Share it to bring people in; anyone holding it can ask to join.
  join_link: string;
  // What's been paid (per currency, and by category), the budget, and both in my currency.
  // What cards will be charged later, in all and by date (Money's Coming up): missing only from an answer a phone kept
  // offline from before.
  money: Omit<Spending, "coming_up"> & {
    coming_up?: Amount[];
    charges?: ComingCharge[];
    budget: Budget | null;
    estimate: Estimate | null;
  };
  // The currency my estimates are in here and where it's from (src/currency.ts): "trip", my member profile's here,
  // set through my AI for this trip alone, or "account", mine for every trip; null when I have none. Missing only
  // from an answer a phone kept offline from before.
  your_currency?: CurrencyInUse | null;
  // The newest change this view includes; live updates say what came after it.
  last_change: number;
  // When locations can be shared (the day before the trip to the day after), and whether that's now.
  location_window: { opens?: string; closes?: string; open: boolean };
  locations: PersonLocation[];
  // Whether the trip may offer to bring the others in: only its owner, alone in it, with enough planned,
  // who hasn't put it away for good. The page decides the rest by its own clock: on a later day than
  // `created_at` in the person's time zone, and not before `snoozed_until` (closed for a week).
  invite: { created_at: string; snoozed_until: string | null } | null;
  // How many spots it has and uses, owner included (features/paying.md, "Spots"). Always sent; missing
  // only from an answer a phone kept offline from before spots, which the page then leaves out.
  seats?: Seats;
  // Read-only, and why (features/paying.md, "Refunds"): its Trip Pass refunded (its owner can get a new one
  // for it at `buy_url`), its payment disputed, or its refund approved and on its way. Null: writable.
  locked?: { why: "refunded" | "disputed" | "refund_pending"; buy_url: string | null } | null;
  // The trip offered by its owner to a member, while it waits (DESIGN.md, Member, "Taking over a trip"): for the owner
  // and that member only (null for anyone else, and when there's none). Missing only from an answer a phone kept
  // offline from before.
  offer?: TripOffer | null;
  // Shared to view outside the trip (features/sharing.md), as this member sees it. Missing only from an answer a phone
  // kept offline from before.
  sharing?: TripSharing;
  // Each member's diet and mobility, for the people in the trip to plan meals and walks around: set in the app only,
  // never by or to an AI (DESIGN.md, "Private to the app, never to an AI"), Trip settings' Diet and mobility. Only those
  // who said either, as text (a list or notes an AI wrote before, said in words). Missing only from an answer a phone
  // kept offline from before.
  needs?: Needs[];
}

// A trip's offer (TripView's `offer`): to whom (their person id) and by whom, until when it waits, and to the member
// it's offered to, what taking it over needs now (src/takeover-words.ts; none while the trip is read-only) and where
// their AI is sent to buy it (the page makes its own link).
export interface TripOffer {
  to: string;
  by: string;
  until: string;
  needs?: TakeoverNeeds & { buy_url: string | null };
}

// One member's diet and mobility (TripView's `needs`).
export interface Needs {
  person: string;
  diet?: string;
  mobility?: string;
}

// A trip's spots: used of `limit` (null: unlimited), whether a let-in would be refused now, the owner's
// plan when that's why it's unlimited ("Frequent Flyer"), what pays for the trip, where its owner adds
// more and what 5 more cost (and a Trip Pass, in cents), and (for the owner) the add-ons bought, each
// refundable until a date while none of its spots was ever needed.
export interface Seats {
  used: number;
  limit: number | null;
  full: boolean;
  plan: string | null;
  coverage: "pass" | "plan" | "legacy";
  add_url: string | null;
  prices: { block: number; pass: number };
  addons: SeatAddon[];
}

export interface SeatAddon {
  id: string;
  blocks: number;
  status: "active" | "refunded" | "disputed";
  bought_at: string;
  used: boolean;
  refundable_until: string | null;
  // Its latest refund request, for its owner (null for anyone else).
  refund_request: RefundRequestState | null;
}

// A trip of mine by name, for the trip menu: the others to switch to (only those I'm in open).
export interface TripName {
  id: string;
  title: string;
  dates: When;
  status: "member" | "requested";
}

// A trip its owner deleted (features/trips-and-members.md): on its owner's Your trips (Recently deleted)
// while it can be restored, and what its page, its join link and its live updates say to the people who
// were in it. Only the owner deletes a trip, so `by` is its owner.
export interface DeletedTrip {
  id: string;
  title: string;
  dates: When;
  by: { id: string; name: string };
  // When it was deleted, and until when its owner can restore it (30 days on); then it's purged.
  at: string;
  restorable_until: string;
}

// Where a join link leads, for the person about to ask.
export interface JoinPreview {
  trip: { id: string; title: string; dates: When };
  owner: { id: string; name: string };
  members: number;
  // No spot left for now (features/paying.md, "Spots"): asking still works, and waits for more.
  full: boolean;
  // Read-only for now (its Trip Pass refunded or disputed, or its refund on its way): nobody new can ask.
  locked: boolean;
  // Already in it, already asked, turned down by its owner (asking again stays possible), or neither yet.
  you: "member" | "requested" | "turned_down" | null;
}

export interface Interest {
  person: string;
  tier: string | null;
  going: string | null;
}

// What the booking form on a card sends (web/api.ts, POST /api/trips/:id/bookings/:item): the version it read,
// and only what it changed, null taking it off (DESIGN.md, Views, "Record a booking where it's made").
export interface BookingEdit {
  version: number;
  when?: When | null;
  cost?: { amount: number; currency: string; per: "person" | "group" } | null;
  // Every payment as it'll be; none: booked, not paid yet.
  paid?: { by: string; amount: number; currency: string; method?: string }[];
  // Every charge later as it'll be (a card on file charged on a day); none: nothing charged later.
  charges?: { by: string; amount: number; currency: string; method?: string; on: string }[];
  provider?: string | null;
  ref?: string | null;
  url?: string | null;
  booked_by?: string | null;
  // The one full-refund step: a date, or a local time in the booking's own time zone.
  free_until?: string | null;
}

export type Item = ItemFields & {
  id: string;
  version: number;
  status: string;
  created: { by: { id: string; name: string }; at: string };
  updated: { by: { id: string; name: string }; at: string; via: string | null };
  interest: Interest[];
};

// Where a card's own link is kept (features/links.md, "Add, edit and remove every link on the web"): its contact's
// website, email or social pages, its booking page, or its other links.
export type OwnLinkField = "website" | "email" | "booking" | "links" | "social";

export interface Link {
  id: string;
  // Changed on the web over the version the page read (features/links.md, "Add, edit and remove every link on the
  // web").
  version: number;
  url: string;
  note: string | null;
  // The items it's about; empty while it waits in the inbox.
  items: string[];
  preview: ShownPreview | null;
  created: { by: { id: string; name: string }; at: string };
}

// What an AI found out that isn't one place: shared, with its sources and what it's about.
export interface Research {
  id: string;
  title: string;
  // The answer, as short bullets (the small Markdown subset).
  text: string;
  sources: string[];
  // The items it's about; empty for the trip in general.
  about: string[];
  created: { by: { id: string; name: string }; at: string; via: string | null };
  updated: { by: { id: string; name: string }; at: string; via: string | null };
}

export interface Pad {
  id: string;
  name: string;
  for: string;
  prompt: string | null;
  // The page to send the friend.
  link: string;
  closed: boolean;
  entries: number;
  not_taken: number;
}

export interface PadEntry {
  id: string;
  pad: string;
  from: string;
  text: string;
  links: string[];
  taken: boolean;
  at: string;
}

// A person's note on an item, the trip, or a member: their own words, with their name.
export interface Note {
  id: string;
  on: string;
  text: string;
  by: { id: string; name: string };
  at: string;
  edited_at?: string;
}

// A question its author put to people, with their answers and who hasn't answered yet.
export interface Ask {
  id: string;
  // What the page answers over (a question changed meanwhile isn't answered as it was).
  version: number;
  by: { id: string; name: string };
  to: { id: string; name: string }[] | "everyone";
  about?: { id: string; title: string };
  text: string;
  options?: string[];
  status: "open" | "closed";
  at: string;
  answers: { id: string; by: { id: string; name: string }; choice?: string; text?: string; at: string }[];
  waiting_on: { id: string; name: string }[];
}

// A reminder Tripmo sends (a notification and an email) at a local time, to people or everyone.
export interface Reminder {
  id: string;
  // What the web's edits are made over (features/tasks-and-reminders.md, "Reminders on the web").
  version: number;
  // Local to its time zone, "2026-11-18T09:00".
  at: string;
  tz: string;
  due_at: string;
  to: { id: string; name: string }[] | "everyone";
  about?: { id: string; title: string };
  text: string;
  sent_at: string | null;
  // Fell due while the trip was deleted: not sent on restore.
  missed_at: string | null;
  // Who set it, and when.
  created: { by: { id: string; name: string }; at: string };
}

// A travel document: whose, what, its status and expiry for everyone; details only where shared. Kept in the app only
// (DESIGN.md, "Private to the app, never to an AI").
export interface TravelDocument {
  id: string;
  // What its owner's edits are made over (the form's if_version). Missing on a shared trip's view, and from an answer
  // a phone kept offline from before.
  version?: number;
  person: { id: string; name: string };
  kind: string;
  title: string;
  status?: string;
  expires?: string;
  details?: Record<string, string | number | boolean>;
  details_hidden?: true;
  shared_with?: string[] | "everyone";
}

export interface TripItems {
  items: Item[];
  links: Link[];
  pads: Pad[];
  entries: PadEntry[];
  notes: Note[];
  asks: Ask[];
  reminders: Reminder[];
  documents: TravelDocument[];
  // Newest first.
  research: Research[];
  // The files this person may see, oldest first.
  files: TripFile[];
}

// A file on an item or a travel document: its bytes are at /api/files/<id>.
export interface TripFile {
  id: string;
  on: string;
  name: string;
  type: string;
  size: number;
  // "photo": added as a picture of its item, shown as the item's photo (before web photos).
  as: "file" | "photo";
  by: { id: string; name: string };
  at: string;
}

// One operation of a change, as the change log keeps it.
export interface ChangeOp {
  op: "create" | "update" | "delete" | "restore";
  record:
    | "item"
    | "interest"
    | "link"
    | "research"
    | "file"
    | "pad"
    | "pad_entry"
    | "note"
    | "ask"
    | "answer"
    | "reminder"
    | "document"
    | "trip"
    | "member"
    // A trip offered to a member, its offer taken back or turned down: `to`, whom it's offered to.
    | "offer";
  id: string;
  // An interest's place.
  item?: string;
  // Whose rating it is, in Tripmo's own change to many people's (migrations 0048, three tiers, and 0050, Yes).
  person?: string;
  // What a note or a file is on.
  on?: string;
  // What an ask is about; an answer's ask.
  about?: string;
  ask?: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown>;
}

// A change in the trip's history: who (a person, or a friend writing in a pad), through what, and why.
export interface Change {
  id: string;
  at: string;
  by: { id: string; name: string } | { friend: string; pad: string; pad_name: string };
  via?: string;
  reason: string;
  ops: ChangeOp[];
  undoes?: string;
  undone_by?: string;
  // Yours, not undone yet, and a kind of change undo puts back.
  can_undo: boolean;
}

export interface ChangePage {
  changes: Change[];
  next_cursor: string | null;
}

// A change that came in while the trip was open: enough to say "Mei changed 3 things".
export interface LiveChange {
  id: string;
  seq: number;
  by: Change["by"];
  via?: string;
  ops: number;
  // A friend's change: whether it only wrote new tips (false: it changed or removed one).
  wrote?: boolean;
}

// A day's weather for where we are (features/weather.md): the forecast once it covers the whole day,
// else what it's usually like then. In °C and mm; the web view shows °F where the browser's region does.
export type WeatherDay = {
  date: string;
  // The place's time zone: its day runs midnight to midnight there, and its days before today are past.
  tz: string;
  // Where it's for: where we sleep, a day trip's places, or the day's places with no stay that night.
  where: { name: string; for: "stay" | "day_trip" | "places" };
} & (
  | {
      kind: "forecast";
      symbol: WeatherSymbol;
      high: number;
      low: number;
      rain_mm: number;
      // When MET Norway last answered for this place.
      checked_at: string;
    }
  | {
      kind: "usual";
      high: number;
      low: number;
      // The share of days with rain (2 mm or more), 0 to 1.
      rain_days: number;
      years: { first: number; last: number };
    }
);

export interface TripWeather {
  days: WeatherDay[];
  // On a moving day, the weather where we leave that morning (Today shows each city's): only those days.
  // Missing from an answer a phone kept offline from before.
  mornings?: WeatherDay[];
}

// Download your data (features/privacy-and-data.md): my latest export from the profile, and when I can ask
// for the next (one a day). `url` downloads it while it's ready (24 hours).
export type ExportState =
  | { status: "none"; next_at: null }
  | {
      id: string;
      status: "pending" | "ready" | "expired" | "failed";
      requested_at: string;
      ready_at: string | null;
      expires_at: string | null;
      size: number | null;
      url: string | null;
      next_at: string | null;
    };

// What Delete my account would do, as its dialog says it (and the admin's dry run).
export interface DeletionPreview {
  user: { id: string; email: string };
  // Trips only I'm in: deleted.
  deleted_trips: { id: string; title: string }[];
  // Trips I own with others: handed to the longest-standing member.
  handed_over: { id: string; title: string; to: { id: string; name: string } }[];
  // Trips someone else owns: I leave, as "a former member".
  left: { id: string; title: string }[];
  // My own records that go, by kind.
  removed: Record<string, number>;
  // Kept for accounting, without my name or email.
  purchases: number;
  // Plans that end now.
  plans_ending: { id: string; kind: string }[];
  unused_passes: number;
  stripe_customer: string | null;
  finishes_at: string;
}

// What helps find a feedback report (src/feedback/diagnostics.ts, which checks it), as the web form sends it while its
// switch is on (features/feedback.md).
export interface FeedbackDiagnostics {
  page?: string;
  version?: string;
  browser?: string;
  device?: string;
  language?: string;
  time?: string;
  time_zone?: string;
  request_id?: string;
  errors?: { request_id: string; status: number; path?: string; at?: string }[];
  // From a page that broke: the error's message and where in Tripmo's code it happened (src/feedback/crash.ts).
  crash?: { message: string; stack: string[] };
}

// A report kept and on its way to support.
export interface FeedbackSent {
  sent: true;
  id: string;
  message: string;
}

// Sharing a trip to view (features/sharing.md): the switches, as the server and Trip settings name them.
export type { Share } from "../sharing/switches.ts";

// A trip's sharing, as its members see it (Trip settings, get_trip's `sharing`): whether it's on, who can view
// (anyone with the link, or only people on a list) and what's shared. Only the owner gets the link, the list's emails
// and the controls; each member gets their own choice for their name, photo and ratings.
export interface TripSharing {
  on: boolean;
  who: "anyone" | "list";
  shares: Share[];
  // How many emails the list has (the owner also gets the emails themselves).
  listed: number;
  // The owner's: the view link while it's on, and the list.
  link?: string | null;
  emails?: string[];
  // The owner's: each other member's choice (by their person id), null until they choose.
  choices?: Record<string, boolean | null>;
  // This member's own choice for their name, photo and ratings: null until they choose (nothing of theirs shows),
  // true for Show them, false for Keep them hidden. The owner's is always null (theirs follow the switches).
  mine: boolean | null;
}

// What a viewer gets of a shared trip (GET /api/view/:code, src/sharing/routes.ts; built by src/sharing/view.ts): the
// trip, named by its view link's code, and only what its switches share. People are pseudonyms of the link.
export interface SharedTrip {
  code: string;
  title: string;
  brief: string;
  dates: When;
  owner: string;
  members: Person[];
  former: Person[];
  // The owner's first name, when names are shared ("Shared by Meiwin"); else null ("Shared with you").
  shared_by: string | null;
  // Tripmo's own sample trip (features/sample-trip.md, "How people reach it"): its bar says "A sample trip".
  sample: boolean;
  who: "anyone" | "list";
  shares: Share[];
  // With Money only.
  budget: Budget | null;
  money: (Spending & { charges: ComingCharge[] }) | null;
  // The map, as the web view draws it (Me's `map`, for a viewer who may be signed out).
  map: { tiles: string; glyphs: string };
  // Who's looking: signed in or not, and in any trip of their own (the bar's way on), and, for a member of this very
  // trip previewing it, the trip's own id (the bar's way back).
  viewer: { signed_in: boolean; in_trips: boolean; member_of: string | null };
}

export interface SharedView {
  trip: SharedTrip;
  items: Item[];
  links: Link[];
  research: Research[];
  notes: Note[];
  files: TripFile[];
  documents: TravelDocument[];
}
